import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUser } from "util/UserContext";
import ChatConversation from "./ChatConversation";
import ChatInbox from "./ChatInbox";
import ChatInboxLoadingShell from "./ChatInboxLoadingShell";
import RequireAuth from "components/Auth/RequireAuth";

/**
 * Route entry for `/chat` and `/chat/:conversationId`. Chat is an auth-only
 * surface: signed-out visitors are shown the shared `RequireAuth` wall so
 * the URL is preserved and they can sign in without losing the targeted
 * conversation. On mobile with a conversation id we render the focused
 * conversation view; otherwise we render the inbox (which itself embeds the
 * conversation on desktop).
 *
 * While the auth context is still hydrating we render the same Instagram-
 * style inbox shell that `ChatInbox` uses during its own fetch, so the user
 * sees the real chrome (search, compose, Messages/Requests) immediately
 * and only the conversation list reads as a loading state.
 */
const ChatPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams<{ conversationId?: string }>();
    const { userIsMobile } = useUser();
    const selectedConversationPublicId = params.conversationId || null;

    // When a specific conversation id is in the URL on mobile, the shell
    // we show during auth-loading is the focused conversation viewport
    // rather than the inbox list. Keep the list shell for the default
    // inbox view so the layout matches what is about to render.
    const loadingFallback =
        userIsMobile && selectedConversationPublicId ? (
            <ChatInboxLoadingShell embedded />
        ) : (
            <ChatInboxLoadingShell />
        );

    return (
        <RequireAuth loadingFallback={loadingFallback}>
            {userIsMobile && selectedConversationPublicId ? (
                <ChatConversation
                    conversationPublicId={selectedConversationPublicId}
                    onBack={() => navigate("/chat")}
                />
            ) : (
                <ChatInbox
                    selectedConversationPublicId={selectedConversationPublicId}
                    onSelectConversation={(conversationPublicId) =>
                        navigate(`/chat/${conversationPublicId}`)
                    }
                    onClearSelection={() => navigate("/chat")}
                />
            )}
        </RequireAuth>
    );
};

export default ChatPage;
